import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { RaffleService } from 'src/app/data/service/raffle.service';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { Log } from '../log';

@Injectable()
export class RaffleGuard implements CanActivate {

  constructor(private raffleService: RaffleService, private router: Router) { }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {

    // if looking for creator, means we are already in a raffle and don't need the rest
    Log.debug('canActivate', next, state);

    if (next.data.accessLevelRequired != null && next.data.accessLevelRequired === 'Creator') {
      const hasAccess = await this.testAccess(next.data.accessLevelRequired);
      if (!hasAccess) {
        ServiceHelper.displayAlert('Insufficient Privileges', 'You do not have required permissions to view this page');
        return false;
      }
      return true;
    }

    // this guard makes sure that a user has a raffle when accessing pages, and if they don't,
    // will use the raffle code from route to load the raffle
    const code = next.paramMap.get('code');

    if (this.raffleService.inRaffle === true && this.raffleService.currentRaffle.accessCode === code) {
      Log.debug('Already in raffle', code);

      if (next.data.accessLevelRequired != null) {
        const hasAccess = await this.testAccess(next.data.accessLevelRequired);
        if (!hasAccess) {
          ServiceHelper.displayAlert('Insufficient Privileges', 'You do not have required permissions to view this raffle');
          return false;
        }
      }
      return true;
    }

    // no code provided, so no raffle, take user back to home page
    if (code == null) {
      Log.debug('No Code');
      this.router.navigateByUrl('/admin/home');
      return false;
    }

    Log.debug('Attempting to load raffle with code', code);
    return this.raffleService.loadRaffleByCode(code).then(async () => {
      Log.debug('Attempt to load raffle with code result', this.raffleService.inRaffle);

      if (this.raffleService.inRaffle) {
        // If an access level was passed in data, read it and validate
        if (next.data.accessLevelRequired != null) {
          const hasAccess = await this.testAccess(next.data.accessLevelRequired);
          if (!hasAccess) {
            ServiceHelper.displayAlert('Insufficient Privileges', 'You do not have required permissions to view manage this raffle');
            this.router.navigateByUrl(`/admin/home`);
          }
          return hasAccess;
        }
        return true;
      }
      this.router.navigateByUrl('/admin/home');
      return false;

    }).catch(error => {
      Log.debug('Attempt to load raffle with code error', error);
      ServiceHelper.displayAlert('Error Loading Raffle', error);
      this.router.navigateByUrl('/admin/home');
      return false;
    });
  }

  private async testAccess(accessLevelRequired: string) {
    Log.debug('Page requires access level', accessLevelRequired);
    let isAdmin = false;
    switch (accessLevelRequired) {
      case 'Admin':
        isAdmin = await this.raffleService.getIsRaffleAdmin();
        Log.debug('is raffle admin: ' + isAdmin);
        break;
      case 'Creator':
        isAdmin = await this.raffleService.getIsRaffleCreator();
        Log.debug('is raffle creator: ' + isAdmin);
        break;
    }
    return isAdmin;
  }

}
